import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Turnstile } from '@marsidev/react-turnstile';

import { useLoginMutation } from '../../../api';
import { selectAuthError, setError } from '../authSlice';
import { toastService } from '../../../utils/toastService';
import LanguageSelector from '../../../layout/LanguageSelector';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getEnv } from '../../../config/runtimeConfig';

const DEFAULT_TURNSTILE_SITE_KEY = '0x4AAAAAAC1zB2mNHZPFB_QU';

/** Yerel/staging API CAPTCHA doğruluyorsa false bırakın. Yalnızca backend devde token istemiyorsa .env'de true yapın. */
const skipTurnstile = getEnv('REACT_APP_SKIP_TURNSTILE') === 'true';

const Login = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const error = useSelector(selectAuthError);
    const [turnstileToken, setTurnstileToken] = useState(null);
    const [turnstileError, setTurnstileError] = useState(false);
    const { th } = useLanguage();
    const turnstileRef = useRef(null);

    const requiresTurnstile = !skipTurnstile;
    const turnstileSiteKey = getEnv('REACT_APP_TURNSTILE_SITE_KEY') || DEFAULT_TURNSTILE_SITE_KEY;

    const [login, { isLoading }] = useLoginMutation();

    useEffect(() => {
        const savedLanguage = localStorage.getItem('selectedLanguage');
        const i18nextLng = localStorage.getItem('i18nextLng');

        sessionStorage.clear();
        localStorage.clear();

        if (savedLanguage) {
            localStorage.setItem('selectedLanguage', savedLanguage);
        }
        if (i18nextLng) {
            localStorage.setItem('i18nextLng', i18nextLng);
        }
    }, []);

    const LoginSchema = Yup.object().shape({
        email: Yup.string()
            .test('is-valid-email', th('auth.emailInvalid'), (value) => {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return emailRegex.test(value) || value === 'sefa.ozturk@karsan.com.tr';
            })
            .required(th('auth.emailRequired')),
        password: Yup.string().min(8, th('auth.passwordMinLength')).required(th('auth.passwordRequired'))
    });

    const resetTurnstile = () => {
        turnstileRef.current?.reset();
        setTurnstileToken(null);
    };

    const handleSubmit = async (values) => {
        try {
            dispatch(setError(null));
            setTurnstileError(false);

            if (requiresTurnstile) {
                if (!turnstileToken) {
                    setTurnstileError(true);
                    toastService.error(th('auth.verifyRecaptcha'));
                    return;
                }
            }

            const loginData = {
                email: values.email,
                password: values.password,
                isLdap: false,
                turnstileToken: requiresTurnstile ? turnstileToken : ''
            };

            const result = await login(loginData).unwrap();

            if (result && result.token) {
                toastService.success(th('auth.loginSuccess'), th('common.success'));

                if (process.env.NODE_ENV === 'development') {
                    console.log('Token expires in:', result.expiresIn, 'seconds');
                    if (result.accessTokenExpiration) {
                        console.log('Access token expires at:', new Date(result.accessTokenExpiration).toLocaleString());
                    }
                }

                navigate('/');
            }
        } catch (err) {
            console.error('Login failed:', err);
            if (requiresTurnstile) {
                resetTurnstile();
            }
        }
    };

    const submitDisabled = isLoading || (requiresTurnstile && !turnstileToken);

    return (
        <div className="surface-0 flex align-items-center justify-content-center min-h-screen" style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', top: 24, right: 32, zIndex: 10 }}>
                <LanguageSelector />
            </div>
            <div className="grid w-30rem mx-auto">
                <div className="col-12 md:col-8 lg:col-6 w-full">
                    <div className="flex justify-content-center mb-5">
                        <img src="/logorenkli.png" alt="Karsan Logo" className="app-logo-normal" style={{ height: '4rem', width: 'auto', marginBottom: '2rem' }} />
                    </div>
                    <Card className="shadow-4">
                        <div className="text-center mb-5">
                            <div className="text-900 text-2xl font-medium mb-3">{th('auth.loginTitle')}</div>
                            <span className="text-600 font-medium">{th('auth.loginSubtitle')}</span>
                        </div>

                        {error && <Message severity="error" text={error} className="w-full mb-4" />}
                        {turnstileError && <Message severity="error" text={th('auth.verifyRecaptcha')} className="w-full mb-4" />}

                        {isLoading && (
                            <div className="flex justify-content-center mb-3">
                                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" fill="var(--surface-ground)" animationDuration=".5s" />
                            </div>
                        )}

                        <Formik initialValues={{ email: '', password: '' }} validationSchema={LoginSchema} onSubmit={handleSubmit}>
                            {({ errors, touched }) => (
                                <Form className="p-fluid">
                                    <div className="field mb-4">
                                        <span className="p-input-icon-left w-full">
                                            <i className="pi pi-envelope"></i>
                                            <Field name="email">
                                                {({ field }) => (
                                                    <InputText
                                                        id="email"
                                                        type="email"
                                                        placeholder={th('auth.email')}
                                                        className={`w-full text-color-secondary surface-50 border-200 ${errors.email && touched.email ? 'p-invalid' : ''}`}
                                                        aria-describedby="email-help"
                                                        autoComplete="email"
                                                        disabled={isLoading}
                                                        {...field}
                                                    />
                                                )}
                                            </Field>
                                        </span>
                                        {errors.email && touched.email && (
                                            <small id="email-help" className="p-error">
                                                {errors.email}
                                            </small>
                                        )}
                                    </div>

                                    <div className="field mb-4">
                                        <span className="p-input-icon-left w-full">
                                            <i className="pi pi-lock"></i>
                                            <Field name="password">
                                                {({ field }) => (
                                                    <InputText
                                                        id="password"
                                                        type="password"
                                                        placeholder={th('auth.password')}
                                                        className={`w-full text-color-secondary surface-50 border-200 ${errors.password && touched.password ? 'p-invalid' : ''}`}
                                                        aria-describedby="password-help"
                                                        disabled={isLoading}
                                                        {...field}
                                                    />
                                                )}
                                            </Field>
                                        </span>
                                        {errors.password && touched.password && (
                                            <small id="password-help" className="p-error">
                                                {errors.password}
                                            </small>
                                        )}
                                    </div>

                                    <div className="flex align-items-center justify-content-between mb-4">
                                        <div className="flex align-items-center">
                                            <Field type="checkbox" id="rememberme" name="rememberme" className="mr-2" disabled={isLoading} />
                                            <label htmlFor="rememberme">{th('auth.rememberMe')}</label>
                                        </div>
                                        <Link to="/forgot-password" className="font-medium no-underline ml-2 text-right cursor-pointer" style={{ color: 'var(--primary-color)' }}>
                                            {th('auth.forgotPassword')}
                                        </Link>
                                    </div>

                                    {requiresTurnstile && (
                                        <div className="flex justify-content-center mb-3">
                                            <Turnstile
                                                ref={turnstileRef}
                                                siteKey={turnstileSiteKey}
                                                onSuccess={(token) => setTurnstileToken(token)}
                                                onExpire={() => setTurnstileToken(null)}
                                                onError={() => setTurnstileToken(null)}
                                                options={{ theme: 'auto', language: 'tr' }}
                                            />
                                        </div>
                                    )}

                                    <Button
                                        type="submit"
                                        label={isLoading ? th('auth.signingIn') : th('auth.login')}
                                        icon={isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-sign-in'}
                                        className="w-full p-3 text-xl"
                                        disabled={submitDisabled}
                                    />
                                </Form>
                            )}
                        </Formik>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Login;
